<template>
  <div
    v-if="unavailableForShop"
    class="stats-widget-data-root grid h-full"
    data-test="stats-widget-multi-source-only"
  >
    <div class="col-12 flex justify-content-center align-items-center text-center">
      <span class="font-semibold">{{ t('statsEditor.widgets.widgetData.render.multiSourceOnly') }}</span>
    </div>
  </div>
  <div
    v-else-if="permissionToWidgetData && !permissionToWidgetData.access"
    class="stats-widget-data-root grid h-full"
  >
    <div class="col-12 flex flex-column">
      <div class="flex justify-content-center align-items-center mb-3 mt-3">
        <div>
          <span class="font-semibold">
            {{ t('widgetLimitation', [t(`offers.plans.${permissionToWidgetData.minimumPlan}`)]) }}
          </span>
          <i
            class="fas fa-star ml-2 text-sm border-circle border-1 p-1"
            :class="`star--${permissionToWidgetData.minimumPlan}`"
          />
        </div>
      </div>
      <div
        v-if="!trialAvailable"
        class="flex justify-content-center align-items-center mb-2"
      >
        <SpmButton
          :label="t('upgradePlanButton')"
          class-style="p-button p-button-primary"
          @click="handleUpgradeButton"
        />
      </div>
      <div
        v-else
        class="flex flex-column justify-content-center align-items-center mb-2"
      >
        <div class="flex align-items-center mb-2">
          <Checkbox
            key="checkbox-acceptTTC"
            v-model="acceptTTC"
            binary
          />
          <label
            class="ml-1"
            for="checkbox-acceptTTC"
          >
            {{ t('payments.acceptTTC') }}
            <a
              href="#"
              data-toggle="modal"
              data-target="#modalCGV"
              class="tc-primary font-bold"
              @click="showTACModal = true"
            >
              {{ t('payments.TTC') }}
            </a>
          </label>
        </div>
        <SpmButton
          :label="t('tryPlan', [t(`offers.plans.${permissionToWidgetData.minimumPlan}`)])"
          :disabled="!acceptTTC"
          class-style="p-button p-button-primary"
          @click="handleTryPlanButton"
        />
      </div>
    </div>
  </div>
  <div
    v-else-if="!permissionToWidgetData || (permissionToWidgetData && permissionToWidgetData.access)"
    class="stats-widget-data-root h-full"
  >
    <div
      v-if="!statLoaded"
      class="grid h-full"
    >
      <div class="col-12">
        <div
          class="p-card card-skeleton p-3"
        >
          <Skeleton
            class="mb-2"
          />
          <Skeleton
            width="10rem"
            class="mb-2"
          />
          <Skeleton
            width="5rem"
            class="mb-2"
          />
          <Skeleton
            height="2rem"
            class="mb-2"
          />
          <Skeleton
            width="10rem"
            height="3rem"
          />
        </div>
      </div>
    </div>
    <div
      v-if="statLoaded"
      class="h-full"
    >
      <div class="col-12 h-full p-0">
        <div class="graph-container px-3 py-2">
          <SpmTableAPI
            v-if="isSpmTable"
            ref="spmTableRef"
            mode="dynamic"
            :columns="[]"
            :dynamic-fetch-fn="fetchDynamicTableData"
            :show-reload-button="true"
            :initial-rows="10"
            :rows-per-page-options="[10, 20, 50, 100]"
            :data-key="tableDataKey"
            :enable-grouped-actions="true"
            :row-actions-options="statsTableRowActions"
            :exportable="findPermission('stats_report.export')"
            :show-export-all-button="true"
            :export-config="{ entityType: 'stats' }"
            @export="handleExport"
          >
            <!-- Composite column: fullname (firstname, lastname, date_creation) -->
            <template #cell-fullname="{ data }">
              <div class="fullname-cell">
                <!-- Show name only if first_name or last_name are configured -->
                <div
                  v-if="shouldShowFirstName || shouldShowLastName"
                  class="name-row"
                >
                  <span
                    v-if="getDisplayName(data)"
                    class="contact-name"
                  >
                    {{ getDisplayName(data) }}
                  </span>
                  <span
                    v-else
                    class="contact-name contact-name--empty"
                  >
                    {{ t('myLists.manage.customers.noName') }}
                  </span>
                  <button
                    v-if="canOpenContactFile(data)"
                    type="button"
                    class="open-contact-link"
                    :title="t('stats.actions.openContactFile')"
                    :aria-label="t('stats.actions.openContactFile')"
                    @click.stop="openContactFileFromRow(data)"
                  >
                    <i class="far fa-arrow-up-right-from-square" />
                  </button>
                </div>
                <!-- Date principale du metric (envoi / ouverture / clic /
                     vente / etc.) — icône + label contextualisé pour ne
                     pas confondre avec une date générique. Format datetime
                     pour conserver l'heure (utile pour discriminer 2 events
                     du même jour). -->
                <div
                  v-if="shouldShowDateInFullname && (data.created_at || data.date_creation)"
                  class="dates-row"
                >
                  <i :class="`${mainDateMeta.icon} dates-row__icon`" />
                  <span class="dates-row__label">{{ t(mainDateMeta.labelKey) }} :</span>
                  <SpmDateDisplay
                    :value="data.created_at || data.date_creation"
                    format="datetime"
                    icon=""
                    size="sm"
                  />
                </div>
              </div>
            </template>

            <!-- Email column -->
            <template #cell-email="{ data }">
              <span v-if="cleanStr(data.email)">
                {{ cleanStr(data.email) }}
              </span>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </template>

            <!-- Campaign Type column -->
            <template #cell-campaign_type="{ data }">
              <SpmBadge
                :label="getCampaignTypeLabel(data.campaign_type)"
                :icon="getCampaignTypeIcon(data.campaign_type)"
                :colors="getCampaignTypeColors(data.campaign_type)"
              />
            </template>

            <!--
              Campaign Name column (nom du scénario/campagne).
              Badge auto/bulk + nom. La résolution du type se fait via :
                - `data.campaign_type` quand présent (cas combined : champ
                  exposé par la UNION ALL côté backend, cf.
                  combined/sending.ts par ex.).
                - `column.subType` sinon (cas automation ou bulk dédié :
                  renseigné par le helper `buildCampaignAutomationNameColumn`
                  / `buildCampaignBulkNameColumn` côté metric).
              cf. libs/common/src/stats/metrics/shared/campaignNameColumn.ts.
            -->
            <template #cell-campaign_name="{ data, column }">
              <div class="campaign-name-cell flex align-items-center">
                <SpmBadge
                  v-if="resolveCampaignNameType(data, column)"
                  :label="getCampaignTypeLabel(resolveCampaignNameType(data, column))"
                  :icon="getCampaignTypeIcon(resolveCampaignNameType(data, column))"
                  :colors="getCampaignTypeColors(resolveCampaignNameType(data, column))"
                />
                <span class="ml-2">{{ data.campaign_name || '—' }}</span>
              </div>
            </template>

            <!-- Channel column -->
            <template #cell-channel="{ data }">
              <SpmBadge
                :label="getChannelLabel(data.channel)"
                :icon="getChannelIcon(data.channel)"
                :colors="getChannelColors(data.channel)"
              />
            </template>

            <!-- Generic slot for all other columns with HTML content -->
            <template
              v-for="col in dynamicColumns"
              :key="`cell-${col.id}`"
              #[`cell-${col.id}`]="{ data, column }"
            >
              <div
                v-if="shouldRenderAsHtml(data, column)"
                v-html="getCellValue(data, column)"
              />
              <template v-else>
                {{ getCellValue(data, column) }}
              </template>
            </template>
          </SpmTableAPI>
          <SpmKpi
            v-else-if="isSpmKpi"
            :kpi-data="kpiData"
            :kpi-name="widgetName"
          />
          <SpmGraph
            v-else
            :name="widgetName"
            :spm-chart="chartData"
            :start-date="startDate"
            :end-date="endDate"
            :metric-id="`${configuration.group}.${configuration.category}.${configuration.metric}`"
            :max-slices="configuration.maxSlices"
          />
        </div>
      </div>
    </div>
  </div>
  <TermsAndConditions
    v-if="showTACModal"
    @on-close-modal="showTACModal = false"
  />
  <SaleAttributionModal
    v-model="saleAttributionModalVisible"
    :id-shop-order="saleAttributionIdShopOrder"
  />
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  Ref,
  ref,
  watch,
} from 'vue';
import SpmGraph from '@/components/graphs/SpmGraph.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SaleAttributionModal from '@/components/modals/SaleAttributionModal.vue';
import SpmKpi from '@/components/graphs/SpmKpi.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import Skeleton from 'primevue/skeleton';
import Checkbox from 'primevue/checkbox';
import Tooltip from 'primevue/tooltip';
import TermsAndConditions from '@/views/auth/TermsAndConditions.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import { fetchIsMultiSourceShop, isMultiSourceOnlyMetric } from '@/composables/data-sources/multiSource';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import {
  getChannelIcon as getChannelIconFromTokens,
  getChannelTagStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';
// eslint-disable-next-line import/no-cycle
import { NestApiType, nestCall } from '@/composables/nestApi';
// eslint-disable-next-line import/no-cycle
import {
  ChartType,
  StatsWidgetType,
} from '@/types/generated-types/graphql';
// eslint-disable-next-line import/no-cycle
import {
  IntervalDateEnum,
  SpmTableColumns,
  SpmTableFilter,
  SpmTableState,
  StringMap,
} from '@/types';
// eslint-disable-next-line import/no-cycle
// eslint-disable-next-line import/no-cycle
import {
  UserState, hasAccessToFeatures, tryPlan, findPermission,
} from '@/composables/User';
import type {
  SpmKpiDataPoint,
  StatsWidgetApiRequest,
  StatsWidgetDataConfiguration,
} from '@/types/stats-types';
import type { ListSettings } from '@/composables/GraphQL';
import { showToastError, showToastSuccess } from '@/helpers';
import type { SpmColumnConfig, ExportPayload, RowActionOption } from '@/components/table-rest-api/types';
import { TypeCampaignEnum } from '@/types';
import { getContact } from '@/composables/data-explorer/DataExplorer';
import { useI18n } from 'vue-i18n';
import {
  CustomKpiMeasureFormat,
  formatCustomKpiValue,
  formatNumberToCurrency,
} from '@/helpers/Number';
import {
  getStatsLabelKeyFromSlug,
  humanizeStatsColumnKey,
  normalizeStatsColumnTranslationKey,
  resolveStatsLabelContextFromField,
  resolveStatsLabelContextFromFormat,
} from '@/helpers/statsLabels';
// eslint-disable-next-line import/no-cycle
import { DEFAULT_CURRENCY } from '@/components/template-builder/utils/constants';
// eslint-disable-next-line import/no-cycle
import { useStore } from '@/store';
// eslint-disable-next-line import/no-cycle
import { defaultPageStateFilters } from '@/store/modules/stats-editor';
import deepcopy from 'deepcopy';
import moment, { DurationInputArg1 } from 'moment';
import DurationConstructor = moment.unitOfTime.DurationConstructor;

interface ChartState {
  type: StatsWidgetType;
  options: Record<string, any>;
  data: {
    labels: string[];
    datasets: any[];
    backgroundColor?: string[];
  };
}

const DEFAULT_STATS_COLORS = [
  '#1bcfb4',
  '#fe9496',
  '#4bcbeb',
  '#9e58ff',
  '#ff68a4',
  '#ff7274',
  '#ff8a44',
  '#ffa600',
];

const normalizePalette = (
  existingColors: Array<string | null | undefined>,
  requiredLength: number,
): string[] => {
  const sanitizedColors = (existingColors ?? []).filter(Boolean) as string[];

  if (!requiredLength) {
    return sanitizedColors.length ? sanitizedColors : [...DEFAULT_STATS_COLORS];
  }

  const filledColors = [...sanitizedColors];

  for (let colorIndex = 0; filledColors.length < requiredLength; colorIndex += 1) {
    filledColors.push(DEFAULT_STATS_COLORS[colorIndex % DEFAULT_STATS_COLORS.length]);
  }

  return filledColors;
};

const applyDefaultChartStyling = (
  labels: string[],
  datasets: any[],
  fallbackType?: ChartType,
) => {
  const styledDatasets = (datasets ?? []).map((dataset: any, datasetIndex: number) => {
    if (!dataset || typeof dataset !== 'object') {
      return dataset;
    }

    const datasetCopy = { ...dataset };
    const datasetType = (datasetCopy.type as ChartType | undefined)
      ?? fallbackType
      ?? ChartType.PIE;

    const normalizedColors = Array.isArray(datasetCopy.backgroundColor)
      ? datasetCopy.backgroundColor
      : datasetCopy.backgroundColor
        ? [datasetCopy.backgroundColor]
        : [];

    const fallbackColor = normalizedColors.find(Boolean)
      ?? DEFAULT_STATS_COLORS[datasetIndex % DEFAULT_STATS_COLORS.length];

    if ([ChartType.PIE, ChartType.DOUGHNUT, ChartType.POLARAREA].includes(datasetType)) {
      const slicesCount = labels.length
        || (Array.isArray(datasetCopy.data) ? datasetCopy.data.length : DEFAULT_STATS_COLORS.length);

      datasetCopy.backgroundColor = normalizePalette(normalizedColors, slicesCount);
    } else if (datasetType === ChartType.LINE) {
      datasetCopy.backgroundColor = [fallbackColor];

      if (!datasetCopy.borderColor) {
        datasetCopy.borderColor = fallbackColor;
      }

      if (datasetCopy.borderWidth === undefined || datasetCopy.borderWidth === null) {
        datasetCopy.borderWidth = 2;
      }
    } else if (datasetType === ChartType.RADAR) {
      datasetCopy.backgroundColor = [fallbackColor];

      if (!datasetCopy.borderColor) {
        datasetCopy.borderColor = fallbackColor;
      }
    } else {
      datasetCopy.backgroundColor = [fallbackColor];
    }

    return datasetCopy;
  });

  return {
    datasets: styledDatasets,
    backgroundColors: [...DEFAULT_STATS_COLORS],
  };
};

export default defineComponent({
  name: 'StatsWidgetData',
  components: {
    SpmTableAPI,
    SaleAttributionModal,
    SpmGraph,
    SpmKpi,
    Skeleton,
    SpmButton,
    Checkbox,
    TermsAndConditions,
    SpmDateDisplay,
    SpmBadge,
  },

  directives: {
    Tooltip,
  },

  props: {
    name: {
      type: [String, Object] as PropType<string | StringMap>,
      required: true,
    },

    configuration: {
      type: Object as PropType<StatsWidgetDataConfiguration>,
      required: true,
    },

    defaultFilters: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    pageName: {
      type: String,
      required: false,
      default: '',
    },
  },

  setup(props) {
    const store = useStore();
    const { t, te, locale } = useI18n();
    const isSpmTable = computed(() => props.configuration.widgetType === StatsWidgetType.TABLE);
    const isSpmKpi = computed(() => props.configuration.widgetType === StatsWidgetType.KPI);
    const isTimeline = computed(() => props.configuration.isTimeline);

    const permissionToWidgetData = computed(() => {
      const { group, category, metric } = props.configuration;
      const featurePermission = hasAccessToFeatures(`${group}.${category}.${metric}`);
      return featurePermission;
    });
    const customSettings = ref<StatsWidgetApiRequest | Record<string, any>>({});
    const tableRequestSettings = ref<ListSettings>({
      limit: 10,
      offset: 0,
      order: [],
      filter: [],
    });

    const statLoaded = ref(false);
    const unavailableForShop = ref(false);

    // For SpmTableAPI dynamic mode
    const spmTableRef = ref();
    const currentColumns = ref<SpmColumnConfig[]>([]);
    // Row actions exposed via SpmTableAPI : kebab "Prévisualiser / Zones de
    // clics" pour les datatables de stats liées à une campagne. Construit
    // dynamiquement depuis la column `type: 'stats-action'` de la response.
    const statsTableRowActions = ref<RowActionOption[]>([]);

    // Modale de détail d'attribution déclenchée depuis le menu kebab d'une
    // ligne de vente (action `showSaleAttributionDetail`).
    const saleAttributionModalVisible = ref(false);
    const saleAttributionIdShopOrder = ref<string | null>(null);

    const setLoading = async (loading: boolean) => {
      if (spmTableRef.value) {
        await spmTableRef.value.setLoading(loading);
      }
    };

    // Keep old refs for backward compatibility
    const tableCols = ref<SpmTableColumns[]>([]);
    const tableData: Ref<SpmTableState | null> = ref({
      items: [],
      isLoading: true,
      total: 0,
      error: '',
    });
    const tableColumnId: Ref<string> = ref('');
    const idShop = UserState.activeShop?.id ?? 0;

    const widgetName = computed(() => (typeof props.name === 'string'
      ? props.name
      : (props.name?.[locale.value] ?? '')));

    // Get 30 last days (default filter for dates)
    const endDate = ref(moment());
    const startDate = ref(moment().subtract(30, 'days'));
    const currentFilters: Ref<any> = ref(deepcopy(defaultPageStateFilters));

    const calculateDateInterval = (filters: any, splitYearPeriod = false) => {
      if (!filters.date_interval) {
        filters.date_interval = deepcopy(defaultPageStateFilters.date_interval);
      }

      const dateInterval = filters.date_interval.value.interval;
      if (dateInterval === IntervalDateEnum.CUSTOM_DATE_RANGE) {
        const { customDateRange } = filters.date_interval.value;
        if (Array.isArray(customDateRange) && customDateRange.length) {
          startDate.value = moment(customDateRange[0]);
          endDate.value = moment(customDateRange[1]);
        }
      } else {
        let intervalValue: DurationInputArg1 = 30;
        let intervalUnit: DurationConstructor = 'days';

        switch (dateInterval) {
          case IntervalDateEnum.LAST_DAY:
            intervalValue = 1;
            break;
          case IntervalDateEnum.LAST_3_DAYS:
            intervalValue = 3;
            break;
          case IntervalDateEnum.LAST_7_DAYS:
            intervalValue = 7;
            break;
          case IntervalDateEnum.LAST_14_DAYS:
            intervalValue = 14;
            break;
          case IntervalDateEnum.LAST_30_DAYS:
            intervalValue = 30;
            break;
          case IntervalDateEnum.LAST_3_MONTHS:
            intervalValue = 3;
            intervalUnit = 'months';
            break;
          case IntervalDateEnum.LAST_12_MONTHS:
            intervalValue = 12;
            intervalUnit = 'months';
            break;
          default:
            intervalValue = 30;
            intervalUnit = 'days';
            break;
        }

        endDate.value = moment();
        startDate.value = moment().subtract(intervalValue, intervalUnit);
      }
    };

    type ExtractedKpiMetrics = {
      primary: number | null;
      primaryType: 'percentage' | 'number' | null;
      secondary: number | null;
      base: number | null;
      hasHtml: boolean;
    };

    const parseNumberFromString = (value: string): number | null => {
      const normalized = value
        .replace(/[\u00a0\s]+/g, '')
        .replace(',', '.');
      const parsed = Number(normalized);

      return Number.isFinite(parsed) ? parsed : null;
    };

    const extractKpiMetrics = (value: unknown): ExtractedKpiMetrics => {
      if (typeof value === 'number') {
        return {
          primary: Number.isFinite(value) ? value : null,
          primaryType: Number.isFinite(value) ? 'number' : null,
          secondary: null,
          base: null,
          hasHtml: false,
        };
      }

      if (typeof value !== 'string') {
        return {
          primary: null,
          primaryType: null,
          secondary: null,
          base: null,
          hasHtml: false,
        };
      }

      const hasHtml = /<[^>]*>/.test(value);
      const strippedValue = value
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ');
      const normalizedValue = strippedValue.replace(/[\u00a0\s]+/g, ' ').trim();

      if (!normalizedValue) {
        return {
          primary: null,
          primaryType: null,
          secondary: null,
          base: null,
          hasHtml,
        };
      }

      const secondaryMatch = normalizedValue.match(/\(([-\d\s.,+-]+)\)/);
      const secondary = secondaryMatch !== null
        ? parseNumberFromString(secondaryMatch[1])
        : null;

      const percentageMatch = normalizedValue.match(/(-?[\d.,]+)\s*%/);
      const percentage = percentageMatch !== null
        ? parseNumberFromString(percentageMatch[1])
        : null;

      let primary: number | null = null;
      let primaryType: 'percentage' | 'number' | null = null;

      if (percentage !== null) {
        primary = percentage;
        primaryType = 'percentage';
      }

      let fallbackSource = normalizedValue;

      if (secondaryMatch) {
        fallbackSource = fallbackSource.replace(secondaryMatch[0], ' ');
      }

      if (percentageMatch) {
        fallbackSource = fallbackSource.replace(percentageMatch[0], ' ');
      }

      if (primary === null) {
        const fallbackMatch = fallbackSource.match(/-?[\d\s]+(?:[.,]\d+)?/);
        if (fallbackMatch) {
          const parsedFallbackValue = parseNumberFromString(fallbackMatch[0]);
          if (parsedFallbackValue !== null) {
            primary = parsedFallbackValue;
            primaryType = 'number';
          }
        }
      }

      if (primary === null && secondary !== null) {
        primary = secondary;
        primaryType = 'number';
      }

      let base: number | null = null;
      if (primaryType === 'percentage' && percentage !== null && secondary !== null) {
        const denominator = percentage / 100;
        if (denominator > 0) {
          base = secondary / denominator;
        } else if (denominator === 0 && secondary === 0) {
          base = 0;
        }
      }

      return {
        primary,
        primaryType,
        secondary,
        base,
        hasHtml,
      };
    };

    const computeFallbackTotal = (
      entries: Array<{ value: unknown; type?: string | null; legend?: string | null; isTotal?: boolean }>,
    ): { value: string; type: string | null; legend: string | null } | null => {
      const nonTotalEntries = entries.filter((entry) => !entry?.isTotal);

      if (!nonTotalEntries.length) {
        return null;
      }

      let aggregatedEntries = 0;
      let aggregatedPrimaryTotal = 0;
      let aggregatedPrimaryCount = 0;
      let aggregatedSecondaryTotal = 0;
      let aggregatedHasSecondary = false;
      let aggregatedPercentageTotal = 0;
      let aggregatedPercentageCount = 0;
      let aggregatedPercentageBaseTotal = 0;
      let aggregatedValueUsesPercentage = false;
      let aggregatedValueHasHtml = false;

      nonTotalEntries.forEach((entry) => {
        const metrics = extractKpiMetrics(entry.value);

        if (metrics.primary !== null || metrics.secondary !== null) {
          aggregatedEntries += 1;
        }

        if (metrics.primary !== null) {
          if (metrics.primaryType === 'percentage') {
            aggregatedValueUsesPercentage = true;
            aggregatedPercentageTotal += metrics.primary;
            aggregatedPercentageCount += 1;

            if (metrics.base !== null && Number.isFinite(metrics.base)) {
              aggregatedPercentageBaseTotal += metrics.base;
            }
          } else {
            aggregatedPrimaryTotal += metrics.primary;
            aggregatedPrimaryCount += 1;
          }
        }

        if (metrics.secondary !== null) {
          aggregatedSecondaryTotal += metrics.secondary;
          aggregatedHasSecondary = true;
        }

        if (metrics.hasHtml) {
          aggregatedValueHasHtml = true;
        }
      });

      if (aggregatedEntries === 0) {
        return null;
      }

      let totalValue: string;

      if (aggregatedValueUsesPercentage) {
        const totalCount = aggregatedHasSecondary
          ? aggregatedSecondaryTotal
          : aggregatedPrimaryTotal;

        const totalBase = aggregatedPercentageBaseTotal;
        let computedPercentage: number;

        if (totalBase > 0) {
          computedPercentage = (totalCount / totalBase) * 100;
        } else if (aggregatedPercentageCount > 0) {
          computedPercentage = aggregatedPercentageTotal / aggregatedPercentageCount;
        } else {
          computedPercentage = 0;
        }

        const normalizedPercentage = Number.isFinite(computedPercentage)
          ? computedPercentage
          : 0;
        const percentageString = `${normalizedPercentage.toFixed(1)}%`;

        if (aggregatedHasSecondary) {
          totalValue = `${percentageString} <div style="color:#babfc3;">(${aggregatedSecondaryTotal})</div>`;
        } else {
          totalValue = percentageString;
        }
      } else if (aggregatedHasSecondary && aggregatedValueHasHtml) {
        const primaryValue = aggregatedPrimaryCount
          ? aggregatedPrimaryTotal
          : aggregatedSecondaryTotal;

        totalValue = `${primaryValue} <div style="color:#babfc3;">(${aggregatedSecondaryTotal})</div>`;
      } else {
        const primaryValue = aggregatedPrimaryCount
          ? aggregatedPrimaryTotal
          : aggregatedSecondaryTotal;

        totalValue = String(primaryValue);
      }

      const referenceEntry = nonTotalEntries[0];
      const hasLegend = nonTotalEntries.some((entry) => !!entry.legend);

      return {
        value: totalValue,
        type: referenceEntry?.type ?? null,
        legend: hasLegend ? 'total' : null,
      };
    };

    const formatStatValue = (value: any, type: string): any => {
      let returnValue = '';

      switch (type) {
        case 'currency':
          returnValue = formatNumberToCurrency(parseFloat(value ?? 0), UserState.activeShop?.currency ?? DEFAULT_CURRENCY);
          break;
        case 'country':
          returnValue = `countries.codes.${value.toUpperCase()}`;
          break;
        case 'lang':
          returnValue = `languages.codes.${value.toLowerCase()}`;
          break;
        default: {
          const context = resolveStatsLabelContextFromFormat(type);
          returnValue = getStatsLabelKeyFromSlug(value, context);
          break;
        }
      }

      return returnValue;
    };

    const toArray = (value: any): any[] => {
      if (value === null || value === undefined || value === '') {
        return [];
      }

      if (Array.isArray(value)) {
        return value
          .map((item: any) => (item && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'id')
            ? item.id
            : item))
          .filter((item: any) => item !== '' && item !== null && item !== undefined);
      }

      if (typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'id')) {
        return [value.id];
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '');
      }

      return [value];
    };

    const normalizeWidgetConfiguration = (configuration: StatsWidgetDataConfiguration) => {
      const normalized = deepcopy(configuration);

      // Supprimer les paramètres frontend-only qui ne doivent pas être envoyés à l'API
      if ('maxSlices' in normalized) {
        delete (normalized as any).maxSlices;
      }

      if (normalized.filters) {
        Object.keys(normalized.filters).forEach((key) => {
          const values = toArray(normalized.filters![key]);
          if (values.length) {
            normalized.filters![key] = values;
          } else {
            delete normalized.filters![key];
          }
        });
      }

      if (Array.isArray(normalized.charts)) {
        normalized.charts = normalized.charts.map((chart: any) => {
          const chartCopy = { ...chart };
          if (chartCopy.filters) {
            Object.keys(chartCopy.filters).forEach((key) => {
              const values = toArray(chartCopy.filters[key]);
              if (values.length) {
                chartCopy.filters[key] = values;
              } else {
                delete chartCopy.filters[key];
              }
            });
          }

          return chartCopy;
        });
      }

      return normalized;
    };

    const buildFiltersPayload = (filters: any) => {
      const normalized = deepcopy(filters && Object.keys(filters).length ? filters : defaultPageStateFilters);

      if (!normalized.date_interval) {
        normalized.date_interval = deepcopy(defaultPageStateFilters.date_interval);
      }

      if (normalized.date_interval.value?.customDateRange) {
        normalized.date_interval.value.customDateRange = normalized.date_interval.value.customDateRange
          .map((date: any) => moment(date).format('YYYY-MM-DD'));
      }

      const keyMap: Record<string, string> = {
        id_campaign_automation: 'automation_campaigns',
      };

      Object.keys(normalized).forEach((key) => {
        const targetKey = keyMap[key] ?? key;
        if (targetKey !== key) {
          normalized[targetKey] = normalized[key];
          delete normalized[key];
        }

        const filter = normalized[targetKey];
        if (!filter || targetKey === 'date_interval') {
          return;
        }

        const valuesArray = toArray(filter.value);
        if (!valuesArray.length) {
          delete normalized[targetKey];
          return;
        }

        filter.value = valuesArray;
      });

      return normalized;
    };

    const cloneListSettings = (settings: ListSettings): ListSettings => ({
      limit: settings.limit,
      offset: settings.offset,
      order: settings.order.map((order) => ({ ...order })),
      filter: settings.filter.map((filter) => ({ ...filter })),
    });

    const buildRequestPayload = (): StatsWidgetApiRequest => {
      const {
        limit, offset, order, filter,
      } = cloneListSettings(tableRequestSettings.value);

      return {
        widgetConfiguration: normalizeWidgetConfiguration(props.configuration),
        filters: buildFiltersPayload(currentFilters.value),
        dateRange: {
          from: startDate.value.format('YYYY-MM-DD'),
          to: endDate.value.format('YYYY-MM-DD'),
        },

        limit,
        offset,
        order,
        filter,
      };
    };

    const fetchWidgetData = async (): Promise<any | null> => {
      const payload = buildRequestPayload();
      customSettings.value = payload;
      const response: any = await nestCall('post', NestApiType.CLIENT_API, '/stats/widget-data', {}, '', payload);

      if (!response || response.error || response.success === false) {
        return null;
      }

      return response.data ?? response;
    };

    /**
     * Construit les `RowActionOption[]` pour SpmTableAPI à partir des
     * columns brutes de la response. Détecte une column avec
     * `type: 'stats-action'` (typiquement `id_stats_*_send`) et mappe
     * chaque entrée d'`actionsText` (ex: `previewSentTemplate`) vers une
     * action concrète qui dispatche l'event store ouvrant le modal de
     * preview/heatmap. Compatible bulk + automation (et combined via
     * fallback AUTOMATION pour les rows sans suffix `_bulk_`).
     */
    const buildStatsTableRowActions = (statsColumns: any[]): RowActionOption[] => {
      const actionColumn = statsColumns.find((c) => c?.type === 'stats-action');
      if (!actionColumn) return [];
      const idField: string = actionColumn.field;
      const actions: string[] = Array.isArray(actionColumn.actionsText)
        ? actionColumn.actionsText
        : [];
      const out: RowActionOption[] = [];
      for (const action of actions) {
        if (action === 'previewSentTemplate') {
          out.push({
            label: t(`stats.actions.${action}`),
            icon: 'far fa-eye',
            visible: (rowData: any) => Boolean(rowData?.[idField]),
            command: (rowData: any) => {
              const idValue = rowData?.[idField];
              if (idValue === undefined || idValue === null) return;
              const isBulk = /_bulk_/.test(idField);
              store.commit('dataViewer/addActiveElement', {
                component: 'DataViewerPreviewSentTemplate',
                title: t('dataViewer.titles.sentId', { id: idValue }),
                configuration: {
                  typeCampaign: isBulk
                    ? TypeCampaignEnum.BULK
                    : TypeCampaignEnum.AUTOMATION,
                  id: idValue,
                  displayHeatMap: true,
                  displayStatistics: true,
                },
              });
            },
          });
        } else if (action === 'openContactFile') {
          out.push({
            label: t(`stats.actions.${action}`),
            icon: 'far fa-user',
            visible: (rowData: any) => Boolean(rowData?.id_shop_contact),
            command: async (rowData: any) => {
              await openContactFileFromRow(rowData);
            },
          });
        } else if (action === 'showWorkflowStats') {
          // Liste de workflows : ouvre la vue arbre stats globale du workflow
          out.push({
            label: t(`stats.actions.${action}`),
            icon: 'far fa-chart-tree-map',
            visible: (rowData: any) => Boolean(
              rowData?.id_campaign_automation || rowData?.id_campaign_bulk,
            ),
            command: (rowData: any) => {
              const idAutomation = rowData?.id_campaign_automation;
              const idBulk = rowData?.id_campaign_bulk;
              const idWorkflow = Number(idAutomation ?? idBulk ?? 0);
              if (!idWorkflow) return;
              const type: 'automation' | 'bulk' = idAutomation
                ? 'automation'
                : 'bulk';
              store.dispatch('dataExplorer/openWorkflowStatsFile', {
                idWorkflow,
                type,
                tabLabel: rowData?.name ? `${rowData.name}` : `Workflow ${idWorkflow}`,
              });
            },
          });
        } else if (action === 'showSaleAttributionDetail') {
          // Tableau de ventes : ouvre la modale qui liste toutes les touches
          // (auto + bulk croisés) ayant potentiellement contribué à la vente.
          out.push({
            label: t(`stats.actions.${action}`),
            icon: 'far fa-sitemap',
            visible: (rowData: any) => Boolean(rowData?.id_shop_order),
            command: (rowData: any) => {
              const idShopOrder = rowData?.id_shop_order;
              if (!idShopOrder) return;
              saleAttributionIdShopOrder.value = String(idShopOrder);
              saleAttributionModalVisible.value = true;
            },
          });
        } else if (action === 'showWorkflowPathForSend') {
          // Tableau d'envois/clics/etc. : ouvre la vue arbre filtrée pour
          // le contact via id_stats_*_send. L'idCampaign est exposé natif
          // dans la rowData (cf. ensureCampaignIdInSelect côté backend).
          out.push({
            label: t(`stats.actions.${action}`),
            icon: 'far fa-route',
            visible: (rowData: any) => {
              const hasSend = Boolean(rowData?.[idField]);
              const hasCampaign = Boolean(
                rowData?.id_campaign_automation || rowData?.id_campaign_bulk,
              );
              return hasSend && hasCampaign;
            },
            command: (rowData: any) => {
              const idStatsSend = rowData?.[idField];
              if (!idStatsSend) return;
              const idAutomation = rowData?.id_campaign_automation;
              const idBulk = rowData?.id_campaign_bulk;
              const idWorkflow = Number(idAutomation ?? idBulk ?? 0);
              if (!idWorkflow) return;
              const type: 'automation' | 'bulk' = idAutomation
                ? 'automation'
                : 'bulk';
              store.dispatch('dataExplorer/openWorkflowStatsFile', {
                idWorkflow,
                type,
                tabLabel: t('workflowStats.tabLabelForContact'),
                initialFilter: { idStatsSend: Number(idStatsSend) },
              });
            },
          });
        }
      }
      return out;
    };

    /**
     * Récupère le `ContactFile` depuis l'id présent dans la rowData et
     * dispatche l'event store qui ouvre la fiche dans le DataExplorer.
     * Réutilisé par le menu kebab et le lien inline dans `cell-fullname`.
     */
    const openContactFileFromRow = async (rowData: any): Promise<void> => {
      const idShopContact = rowData?.id_shop_contact;
      if (!idShopContact) return;
      try {
        const contact = await getContact(Number(idShopContact));
        if (contact) {
          store.dispatch('dataExplorer/openContactFile', { contact });
        }
      } catch (err) {
        console.error('[StatsWidgetData] openContactFileFromRow failed', err);
      }
    };

    const canOpenContactFile = (rowData: any): boolean => Boolean(
      rowData?.id_shop_contact,
    );

    /**
     * Traduit l'en-tête d'une colonne de datatable stats.
     *  1. clé i18n déjà complète et qualifiée (colonnes natives « table
     *     extensions », ex. `stats.tableExtensions.contact.country`). Le test
     *     du point évite de matcher par erreur une clé racine pour un header
     *     brut (`date`, `firstName`...).
     *  2. clé de colonne native normalisée (`stats.columns.*`).
     *  3. sinon : libellé humain conservé tel quel (data custom « Def • Champ »)
     *     ou humanisation de la clé brute (ex. `firstName` -> « First Name »).
     */
    const translateColumnHeader = (rawHeaderKey: string | null | undefined): string => {
      const headerKey = rawHeaderKey ?? '';
      if (!headerKey) {
        return '';
      }
      if (headerKey.includes('.') && te(headerKey)) {
        return t(headerKey);
      }
      const translationKey = normalizeStatsColumnTranslationKey(headerKey);
      if (translationKey && te(translationKey)) {
        return t(translationKey);
      }
      if (/\s/.test(headerKey) || headerKey.includes('•')) {
        return headerKey;
      }
      return humanizeStatsColumnKey(headerKey) ?? headerKey;
    };

    /**
     * Transform stats columns to SpmColumnConfig format
     * Groups related fields into composite columns (fullname, location)
     */
    const transformStatsColumnsToSpmConfig = (statsColumns: any[]): SpmColumnConfig[] => {
      const processedFields = new Set<string>();
      const result: SpmColumnConfig[] = [];

      // Helper to build field config
      const buildFieldConfig = (column: any): any => {
        const field = column.field ?? column.header ?? '';
        const headerKey = column.header ?? field;
        const translatedHeader = translateColumnHeader(headerKey);

        const fieldConfig: any = {
          name: field,
          label: translatedHeader,
        };

        // Add sortable if column supports it
        if (column.sortable === true || column.sortable === 'true' || column.sortable === 1) {
          fieldConfig.sortable = true;
        }

        // Add filterable if column supports it
        if (column.filterable === true || column.filterable === 'true' || column.filterable === 1) {
          fieldConfig.filterable = true;

          // Build filter config based on filterSettings
          const filterConfig: any = {
            type: 'textInput', // Default
          };

          if (column.filterSettings) {
            // Map filter type from API to SpmTableAPI format
            if (column.filterSettings.type === 'dropdown' && column.filterSettings.options) {
              filterConfig.type = 'singleSelect';
              filterConfig.options = column.filterSettings.options.map((opt: any) => ({
                label: opt.label ?? opt.value ?? '',
                value: opt.value ?? opt.label ?? '',
              }));
            } else if (column.filterSettings.type === 'date' || column.type === 'date' || column.type === 'datetime') {
              filterConfig.type = 'dateRange';
            } else if (column.type === 'number' || column.type === 'numeric') {
              filterConfig.type = 'numberInput';
            }
          }

          fieldConfig.filterConfig = filterConfig;
        }

        return fieldConfig;
      };

      // Check if we should create fullname composite
      const firstNameColumn = statsColumns.find((col) => (col.field ?? col.header) === 'first_name');
      const lastNameColumn = statsColumns.find((col) => (col.field ?? col.header) === 'last_name');
      const createdAtColumn = statsColumns.find((col) => (col.field ?? col.header) === 'created_at');
      const dateCreationColumn = statsColumns.find((col) => (col.field ?? col.header) === 'date_creation');

      // Only create fullname composite if at least one name field exists
      const hasNameFields = firstNameColumn || lastNameColumn;

      if (hasNameFields) {
        const fields: any[] = [];

        // Add first_name and last_name to fields array if they exist
        if (firstNameColumn) {
          processedFields.add('first_name');
          fields.push(buildFieldConfig(firstNameColumn));
        }

        if (lastNameColumn) {
          processedFields.add('last_name');
          fields.push(buildFieldConfig(lastNameColumn));
        }

        // Add created_at or date_creation to fullname if name fields exist
        if (createdAtColumn) {
          processedFields.add('created_at');
          fields.push(buildFieldConfig(createdAtColumn));
        } else if (dateCreationColumn) {
          processedFields.add('date_creation');
          fields.push(buildFieldConfig(dateCreationColumn));
        }

        result.push({
          id: 'fullname',
          header: t('myLists.manage.customers.headers.fullName'),
          fields,
        } as SpmColumnConfig);
      }
      // If no name fields but date exists, it will be processed as a regular column below

      // Process all other columns
      statsColumns.forEach((column) => {
        const field = column.field ?? column.header ?? '';

        // Skip if already processed
        if (processedFields.has(field)) {
          return;
        }

        // Check for location composite (country + lang)
        if (field === 'country') {
          const langCol = statsColumns.find((col) => (col.field ?? col.header) === 'lang');
          if (langCol) {
            // Create composite location column
            processedFields.add('country');
            processedFields.add('lang');

            result.push({
              id: 'location',
              header: t('myLists.manage.customers.headers.location'),
              fields: [
                buildFieldConfig(column),
                buildFieldConfig(langCol),
              ],
            } as SpmColumnConfig);
            return;
          }
        }

        // Skip fields that will be part of composite columns
        if (field === 'last_name' || field === 'created_at' || field === 'date_creation' || field === 'lang') {
          const hasFirstName = statsColumns.some((col) => (col.field ?? col.header) === 'first_name');
          const hasLastName = statsColumns.some((col) => (col.field ?? col.header) === 'last_name');
          const hasCountry = statsColumns.some((col) => (col.field ?? col.header) === 'country');
          const hasAnyNameField = hasFirstName || hasLastName;

          if ((field === 'last_name' && hasFirstName)
              || ((field === 'created_at' || field === 'date_creation') && hasAnyNameField)
              || (field === 'lang' && hasCountry)) {
            // Will be processed as part of composite
            return;
          }
        }

        // Regular single-field column
        processedFields.add(field);
        const fieldConfig = buildFieldConfig(column);
        const translatedHeader = translateColumnHeader(column.header ?? field);

        // Les colonnes id (`id` ou `id_*`) sont toujours hidden — elles
        // restent dans la response (utiles pour le rowKey + payload des
        // row actions) mais ne sont pas affichées en tant que cellule.
        // Le rendu de la colonne d'action (kebab "Prévisualiser/Heatmap")
        // est géré séparément par SpmTableAPI via `rowActionsOptions`.
        const isIdColumn = field === 'id' || field.startsWith('id_');

        result.push({
          id: isIdColumn ? 'id' : field,
          header: isIdColumn ? '' : translatedHeader,
          fields: [fieldConfig],
          hidden: isIdColumn,
        } as SpmColumnConfig);
      });

      return result;
    };

    /**
     * Fetch function for SpmTableAPI dynamic mode
     */
    const fetchDynamicTableData = async (params: any) => {
      // Update table request settings with params from SpmTableAPI
      tableRequestSettings.value = {
        limit: params.limit || 10,
        offset: params.offset || 0,
        order: params.order || [],
        filter: params.filters || [],
      };

      const result = await fetchWidgetData();

      if (!result) {
        return {
          columns: [],
          data: [],
          total: 0,
        };
      }

      // Extract data
      let items: any[] = [];
      if (Array.isArray(result.data)) {
        items = result.data;
      } else if (result.data?.items && Array.isArray(result.data.items)) {
        items = result.data.items;
      }

      // Extract columns
      const metadataColumns = result.data?.columns ?? result.columns ?? [];
      let columns: any[] = Array.isArray(metadataColumns) ? metadataColumns : [];

      // Auto-generate columns from data if not provided
      if (!columns.length && items.length) {
        columns = Object.keys(items[0]).map((field) => ({
          field,
          header: field,
        }));
      }

      // Détecte une éventuelle column `type: 'stats-action'` (envoyée par
      // le backend pour les widgets table liés à une campagne) et construit
      // les `rowActionsOptions` correspondantes. SpmTableAPI rend alors
      // automatiquement la colonne kebab à droite — pas besoin de la
      // déclarer dans `currentColumns` (elle reste hidden via isId).
      statsTableRowActions.value = buildStatsTableRowActions(columns);

      // Transform columns to SpmColumnConfig
      const spmColumns = transformStatsColumnsToSpmConfig(columns);
      currentColumns.value = spmColumns;

      // En mode pagination optimisée, le back renvoie `totalRecords: null`
      // + `hasMore: boolean`. SpmTableAPI utilise uniquement `total` pour
      // décider si une page suivante existe — on gonfle artificiellement
      // ce total de `+1` quand `hasMore=true` pour que PrimeVue Paginator
      // active le bouton Next. `hasMore` est lu dans `data.*` (path stats
      // standard) ou `metadata.*` (tableaux bruts data custom dont `data`
      // est un array).
      const responseHasMore = Boolean(
        result.data?.hasMore ?? result.metadata?.hasMore,
      );
      const responseSortingDisabled = Boolean(
        result.data?.sortingDisabled ?? result.metadata?.sortingDisabled,
      );
      const exactTotal = result.metadata?.totalRecords;
      const currentOffset = Number(params?.offset ?? 0);
      const computedTotal =
        exactTotal !== null && exactTotal !== undefined
          ? exactTotal
          : currentOffset + items.length + (responseHasMore ? 1 : 0);

      return {
        columns: spmColumns,
        data: items,
        total: computedTotal,
        hasMore: responseHasMore,
        sortingDisabled: responseSortingDisabled,
      };
    };

    const getTable = async (additionalParams: ListSettings | null = null) => {
      if (additionalParams) {
        tableRequestSettings.value = cloneListSettings(additionalParams);
      } else {
        tableRequestSettings.value = cloneListSettings({
          ...tableRequestSettings.value,
          offset: 0,
        });
      }

      const result = await fetchWidgetData();

      if (!result) {
        tableData.value = {
          items: [],
          total: 0,
          error: '',
          isLoading: false,
        };
        tableCols.value = [];
        tableColumnId.value = '';
        statLoaded.value = true;
        return;
      }

      let items: any[] = [];
      if (Array.isArray(result.data)) {
        items = result.data;
      } else if (result.data?.items && Array.isArray(result.data.items)) {
        items = result.data.items;
      }

      const metadataColumns = result.data?.columns ?? result.columns ?? [];
      let columns: any[] = Array.isArray(metadataColumns) ? metadataColumns : [];

      if (!columns.length && items.length) {
        columns = Object.keys(items[0]).map((field) => ({
          field,
          header: field,
          sortable: false,
          filterable: false,
          editable: false,
          style: '',
          type: 'text',
        }));
      }

      const columnContexts = new Map<string, ReturnType<typeof resolveStatsLabelContextFromField>>();

      const normalizeColumnBoolean = (value: any, fallback = false) => {
        if (value === undefined || value === null) {
          return fallback;
        }

        if (typeof value === 'boolean') {
          return value;
        }

        if (typeof value === 'string') {
          const lowered = value.trim().toLowerCase();
          if (['true', '1', 'yes', 'on'].includes(lowered)) {
            return true;
          }
          if (['false', '0', 'no', 'off'].includes(lowered)) {
            return false;
          }
          return true;
        }

        if (typeof value === 'number') {
          return value !== 0;
        }

        return Boolean(value);
      };

      const normalizedColumns = columns.map((column: any) => {
        const columnCopy = { ...column };

        const sortableValue = columnCopy.sortable ?? columnCopy.sort ?? columnCopy.orderable;
        columnCopy.sortable = normalizeColumnBoolean(sortableValue, false);
        columnCopy.sort = columnCopy.sortable;
        columnCopy.orderable = columnCopy.sortable;

        const filterableValue = columnCopy.filterable ?? columnCopy.filter;
        columnCopy.filterable = normalizeColumnBoolean(filterableValue, false);
        columnCopy.filter = columnCopy.filterable;

        const context = resolveStatsLabelContextFromField(columnCopy.field ?? columnCopy.header ?? '');

        if (columnCopy.field) {
          columnContexts.set(columnCopy.field, context);
        }

        if (
          columnCopy.translatable
          && columnCopy.filterSettings
          && Array.isArray(columnCopy.filterSettings.options)
        ) {
          columnCopy.filterSettings = {
            ...columnCopy.filterSettings,
            options: columnCopy.filterSettings.options.map((option: any) => {
              if (!option || typeof option !== 'object') {
                return option;
              }

              const optionCopy = { ...option };
              optionCopy.label = getStatsLabelKeyFromSlug(
                optionCopy.label ?? optionCopy.value ?? '',
                context,
              );

              return optionCopy;
            }),
          };
        }

        return columnCopy;
      });

      const translatableColumns = normalizedColumns.filter(
        (column: any) => column.translatable && column.field,
      );

      const customMeta = (result.metadata as any)?.customMeta as
        | { skipLabelTranslation?: boolean; measureFormats?: CustomKpiMeasureFormat[] }
        | undefined;
      const skipLabelTranslation = customMeta?.skipLabelTranslation === true;
      const measureFormats = customMeta?.measureFormats ?? [];
      const shopCurrency = UserState.activeShop?.currency ?? DEFAULT_CURRENCY;

      const normalizedItems = items.map((item) => {
        if (!item || typeof item !== 'object') {
          return item;
        }

        const updatedItem: Record<string, any> = { ...item };

        if (!skipLabelTranslation) {
          translatableColumns.forEach((column: any) => {
            const { field } = column;

            if (!field || !Object.prototype.hasOwnProperty.call(updatedItem, field)) {
              return;
            }

            const cellValue = updatedItem[field];
            const context = columnContexts.get(field);

            if (Array.isArray(cellValue)) {
              updatedItem[field] = cellValue.map((entry: any) => getStatsLabelKeyFromSlug(entry, context));
            } else {
              updatedItem[field] = getStatsLabelKeyFromSlug(cellValue, context);
            }
          });
        }

        if (measureFormats.length) {
          measureFormats.forEach((mf, i) => {
            const field = `measure_${i + 1}`;
            if (Object.prototype.hasOwnProperty.call(updatedItem, field)) {
              updatedItem[field] = formatCustomKpiValue(
                updatedItem[field],
                mf,
                shopCurrency,
              );
            }
          });
        }

        return updatedItem;
      });

      tableCols.value = normalizedColumns.map((column: any) => {
        const headerKey = column.header ?? column.field ?? '';
        const translatedHeader = translateColumnHeader(headerKey);

        return {
          sortable: false,
          filterable: false,
          editable: false,
          style: '',
          type: 'text',
          ...column,
          header: translatedHeader,
        } as SpmTableColumns;
      });

      if (tableRequestSettings.value.filter.length > 0) {
        tableRequestSettings.value.filter.forEach((filter: SpmTableFilter) => {
          const col = tableCols.value.find((column: SpmTableColumns) => column.field === filter.field);
          if (col) {
            col.filterValue = filter;
          }
        });
      } else {
        tableCols.value.forEach((column: SpmTableColumns) => {
          if (column.filterValue) {
            column.filterValue = undefined;
          }
        });
      }

      if (tableCols.value.length) {
        const idColumn = tableCols.value.find((column) => column.isId);
        tableColumnId.value = idColumn ? idColumn.field : tableCols.value[0].field;
      } else {
        tableColumnId.value = '';
      }

      // `data.*` est utilisé par le path stats standard (data est un objet) ;
      // `metadata.*` par les tableaux bruts data custom (data est un array,
      // donc on ne peut pas y attacher les drapeaux de pagination).
      const responseHasMore = Boolean(
        result.data?.hasMore ?? result.metadata?.hasMore,
      );
      const responseSortingDisabled = Boolean(
        result.data?.sortingDisabled ?? result.metadata?.sortingDisabled,
      );

      // En mode pagination optimisée (back renvoie `totalRecords: null`),
      // PrimeVue Paginator a besoin d'un `total` cohérent pour activer/
      // désactiver le bouton Next. On le calcule à partir de l'offset
      // courant + items.length, plus 1 si `hasMore` indique qu'au moins
      // une ligne suivante existe.
      const currentOffset = Number(tableRequestSettings.value?.offset ?? 0);
      const exactTotal = result.metadata?.totalRecords;
      const computedTotal =
        exactTotal !== null && exactTotal !== undefined
          ? exactTotal
          : currentOffset
            + normalizedItems.length
            + (responseHasMore ? 1 : 0);

      tableData.value = {
        items: normalizedItems,
        total: computedTotal,
        error: '',
        isLoading: false,
        hasMore: responseHasMore,
        sortingDisabled: responseSortingDisabled,
      };

      statLoaded.value = true;
    };

    const createDefaultChartState = (): ChartState => ({
      type: props.configuration.widgetType,
      options: {},
      data: { labels: [], datasets: [] },
    });

    const chartData = ref<ChartState>(createDefaultChartState());

    const getChart = async () => {
      const result = await fetchWidgetData();

      if (!result) {
        chartData.value = createDefaultChartState();
        statLoaded.value = true;
        return;
      }

      let chartOptions: Record<string, any> = result.chartOptions ?? {};
      if (typeof chartOptions === 'string') {
        try {
          chartOptions = JSON.parse(chartOptions);
        } catch (error) {
          chartOptions = {};
        }
      }

      let labels: string[] = [];
      let datasets: any[] = [];

      if (result.data?.labels || result.data?.datasets) {
        labels = (result.data.labels ?? [])
          .map((label: any) => (label !== null && label !== undefined ? String(label) : ''));
        datasets = Array.isArray(result.data.datasets) ? result.data.datasets : [];
      } else if (Array.isArray(result.data)) {
        datasets = result.data;
      }

      if ((!labels || !labels.length) && datasets.length && Array.isArray(datasets[0]?.data)) {
        labels = datasets[0].data.map((_: any, index: number) => `${index + 1}`);
      }

      const hasFormatValue = Object.prototype.hasOwnProperty.call(props.configuration, 'formatValue');
      const formatValueType = hasFormatValue ? props.configuration.formatValue ?? '' : undefined;

      if (labels?.length) {
        labels = labels.map((label: string | null) => {
          const value = label ?? 'unknown';

          if (formatValueType !== undefined) {
            return formatStatValue(value, formatValueType);
          }

          return getStatsLabelKeyFromSlug(value);
        });
      }

      if (datasets.length) {
        datasets = datasets.map((dataset: any) => {
          if (!dataset || typeof dataset !== 'object') {
            return dataset;
          }

          const datasetCopy = { ...dataset };

          if (datasetCopy.label !== undefined && datasetCopy.label !== null) {
            datasetCopy.label = getStatsLabelKeyFromSlug(datasetCopy.label);
          }

          return datasetCopy;
        });
      }

      const primaryChartType = (datasets.find((dataset: any) => dataset?.type)?.type as ChartType | undefined)
        ?? ChartType.PIE;

      const { datasets: styledDatasets, backgroundColors } = applyDefaultChartStyling(
        labels,
        datasets,
        primaryChartType,
      );

      chartData.value = {
        type: props.configuration.widgetType,
        options: chartOptions,
        data: {
          labels,
          datasets: styledDatasets,
          backgroundColor: backgroundColors,
        },
      };

      statLoaded.value = true;
    };

    const kpiData: Ref<SpmKpiDataPoint[]> = ref([]);
    const getKpi = async () => {
      kpiData.value = [];
      const result = await fetchWidgetData();

      if (!result) {
        statLoaded.value = true;
        return;
      }

      const values = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result.data?.values)
          ? result.data.values
          : [];

      const customMeta = (result.metadata as any)?.customMeta as
        | { skipLabelTranslation?: boolean; measureFormats?: any[] }
        | undefined;
      const skipLabelTranslation = customMeta?.skipLabelTranslation === true;
      const firstMeasureFormat = customMeta?.measureFormats?.[0];

      const shopCurrency = UserState.activeShop?.currency ?? DEFAULT_CURRENCY;

      // Les valeurs sont désormais formatées par l'API, on ne les formate plus côté frontend
      values.forEach((kpi: any) => {
        const formattedValue = firstMeasureFormat
          ? formatCustomKpiValue(kpi.value, firstMeasureFormat, shopCurrency)
          : kpi.value === null || kpi.value === undefined
            ? ''
            : String(kpi.value);

        const normalizedKpi: SpmKpiDataPoint = {
          type: kpi.type || null,
          color: kpi.color || null,
          icon: kpi.icon || null,
          label: kpi.label || null,
          value: formattedValue,
          legend: kpi.legend || null,
        };

        if (kpi.isTotal) {
          normalizedKpi.isTotal = true;
        }

        if (skipLabelTranslation) {
          normalizedKpi.skipLabelTranslation = true;
        }

        kpiData.value.push(normalizedKpi);
      });

      const includeTotal = Boolean(props.configuration.includeTotal);
      const totalOnly = includeTotal && Boolean(props.configuration.totalOnly);

      if (includeTotal) {
        const totalEntries = kpiData.value.filter((kpi) => kpi.isTotal);

        if (!totalEntries.length) {
          const fallbackTotal = computeFallbackTotal(values as any[]);

          if (fallbackTotal) {
            const normalizedTotalValue = firstMeasureFormat
              ? formatCustomKpiValue(
                fallbackTotal.value,
                firstMeasureFormat,
                UserState.activeShop?.currency ?? DEFAULT_CURRENCY,
              )
              : fallbackTotal.value === null || fallbackTotal.value === undefined
                ? ''
                : String(fallbackTotal.value);

            const fallbackKpi: SpmKpiDataPoint = {
              type: fallbackTotal.type ?? null,
              color: null,
              icon: null,
              label: 'total',
              value: normalizedTotalValue,
              legend: fallbackTotal.legend,
              isTotal: true,
            };

            if (skipLabelTranslation) {
              fallbackKpi.skipLabelTranslation = true;
            }

            if (totalOnly) {
              kpiData.value = [fallbackKpi];
            } else {
              kpiData.value.unshift(fallbackKpi);
            }
          } else if (totalOnly) {
            kpiData.value = [];
          }
        } else if (totalOnly) {
          kpiData.value = totalEntries;
        }
      }

      statLoaded.value = true;
    };

    const getData = async () => {
      // Widget réservé aux boutiques multi-sources (règle du header) : avec une
      // seule source active, un message remplace les données, sans appel d'API.
      if (isMultiSourceOnlyMetric(props.configuration)) {
        unavailableForShop.value = !(await fetchIsMultiSourceShop());
        if (unavailableForShop.value) {
          statLoaded.value = true;
          return;
        }
      }

      if (!permissionToWidgetData.value || (permissionToWidgetData.value && permissionToWidgetData.value.access)) {
        statLoaded.value = false;
        if (isSpmTable.value) {
          await getTable();
        } else if (isSpmKpi.value) {
          await getKpi();
        } else {
          await getChart();
        }
      } else {
        statLoaded.value = true;
      }
    };

    const applyFilters = (newFilters: any) => {
      const filters = deepcopy(
        newFilters && Object.keys(newFilters).length
          ? newFilters
          : store.getters['statsEditor/getPageFilters'] ?? defaultPageStateFilters,
      );

      calculateDateInterval(filters, isTimeline.value);
      currentFilters.value = filters;
    };

    onMounted(() => {
      const initialFilters = Object.keys(props.defaultFilters).length
        ? props.defaultFilters
        : store.getters['statsEditor/getPageFilters'];
      applyFilters(initialFilters);
      getData();
    });

    watch(() => store.getters['statsEditor/getPageFilters'], (newFilters) => {
      // Ne déclencher que si les filtres ont vraiment changé par rapport à currentFilters
      if (JSON.stringify(newFilters) === JSON.stringify(currentFilters.value)) {
        return;
      }
      applyFilters(newFilters);
      getData();
    }, { deep: true });

    watch(() => store.getters['statsEditor/getRefreshWidgetsCount'], () => {
      getData();
    });

    watch(() => permissionToWidgetData, () => {
      getData();
    }, { deep: true });
    const trialAvailable = computed(() => {
      if (UserState.activeOffer) {
        if (!UserState.activeOffer.trials) {
          return true;
        }
        if (permissionToWidgetData.value) {
          if (!UserState.activeOffer.trials[permissionToWidgetData.value.minimumPlan]) {
            return true;
          }
        }
        return false;
      }
      return false;
    });

    const acceptTTC = ref(false);
    const showTACModal = ref(false);

    const handleUpgradeButton = () => {
      store.commit('choosePlan/setIsVisible', true);
    };

    const handleTryPlanButton = async () => {
      if (permissionToWidgetData.value) {
        store.commit('general/showTheSpinner');
        await tryPlan(permissionToWidgetData.value.minimumPlan);
        store.commit('general/hideTheSpinner');
      }
    };

    // Helper functions for cell templates
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    // Check which fields should be displayed in fullname column
    const shouldShowFirstName = computed(() => {
      const fullnameColumn = currentColumns.value.find((col) => col.id === 'fullname');
      if (!fullnameColumn || !fullnameColumn.fields) return false;
      return fullnameColumn.fields.some((field: any) => field.name === 'first_name');
    });

    const shouldShowLastName = computed(() => {
      const fullnameColumn = currentColumns.value.find((col) => col.id === 'fullname');
      if (!fullnameColumn || !fullnameColumn.fields) return false;
      return fullnameColumn.fields.some((field: any) => field.name === 'last_name');
    });

    const shouldShowDateInFullname = computed(() => {
      const fullnameColumn = currentColumns.value.find((col) => col.id === 'fullname');
      if (!fullnameColumn || !fullnameColumn.fields) return false;
      return fullnameColumn.fields.some((field: any) => field.name === 'created_at' || field.name === 'date_creation');
    });

    /**
     * Icône + label contextualisés pour la date affichée sous le contact
     * dans la composite column `cell-fullname`. La date principale d'un
     * widget stats correspond au type d'event : envoyé / ouvert / cliqué /
     * acheté / rebond / etc. — l'icône calendrier générique perdait cette
     * info, on la rétablit via un mapping sur la `category` du widget.
     */
    const mainDateMeta = computed<{ icon: string; labelKey: string }>(() => {
      const category = String(props.configuration?.category ?? '').toLowerCase();
      const map: Record<string, { icon: string; labelKey: string }> = {
        sending: { icon: 'far fa-paper-plane', labelKey: 'stats.dateLabels.sentAt' },
        deliverability: { icon: 'far fa-envelope-circle-check', labelKey: 'stats.dateLabels.deliveredAt' },
        openings: { icon: 'far fa-envelope-open', labelKey: 'stats.dateLabels.openedAt' },
        clicks: { icon: 'far fa-arrow-pointer', labelKey: 'stats.dateLabels.clickedAt' },
        sales: { icon: 'far fa-cart-shopping', labelKey: 'stats.dateLabels.purchasedAt' },
        bounces: { icon: 'far fa-circle-exclamation', labelKey: 'stats.dateLabels.bouncedAt' },
        unsubscribes: { icon: 'far fa-user-slash', labelKey: 'stats.dateLabels.unsubscribedAt' },
        spams: { icon: 'far fa-flag', labelKey: 'stats.dateLabels.spammedAt' },
        registrations: { icon: 'far fa-user-plus', labelKey: 'stats.dateLabels.registeredAt' },
        games: { icon: 'far fa-gamepad', labelKey: 'stats.dateLabels.gameAt' },
      };
      return map[category] ?? {
        icon: 'far fa-calendar-circle-plus',
        labelKey: 'stats.dateLabels.eventAt',
      };
    });

    // Helper to format fullname based on which fields are configured
    const getDisplayName = (data: any) => {
      const firstName = shouldShowFirstName.value ? cleanStr(data.first_name) : '';
      const lastName = shouldShowLastName.value ? cleanStr(data.last_name) : '';
      return formatFullName(firstName, lastName);
    };

    // Channel helpers (same as ListsRejects.vue)
    const channelNumberToName: Record<string, string> = {
      1: 'email',
      2: 'sms',
      3: 'popup',
      4: 'embed',
      5: 'facebookmessenger',
      6: 'pushnotifications',
      7: 'whatsapp',
      8: 'rcs',
    };

    const getChannelLabel = (channel: string | number): string => {
      const channelStr = String(channel);
      const channelName = channelNumberToName[channelStr] || channelStr;
      const translationKey = `channels.${channelName}`;
      return te(translationKey) ? t(translationKey) : channelName;
    };

    const getChannelIcon = (channel: string | number): string => {
      const channelStr = String(channel);
      const channelName = channelNumberToName[channelStr] || channelStr;
      return getChannelIconFromTokens(channelName) || 'fa-solid fa-question';
    };

    const getChannelColors = (channel: string | number): { bg: string; color: string; borderColor?: string } => {
      const channelStr = String(channel);
      const channelName = channelNumberToName[channelStr] || channelStr;
      const vars = getChannelTagStyleVars(channelName);
      return {
        bg: vars['--channel-bg'] || 'var(--surface-200)',
        color: vars['--channel-color'] || 'var(--text-color)',
        borderColor: vars['--channel-border'],
      };
    };

    // Campaign Type helpers
    const getCampaignTypeLabel = (campaignType: string): string => {
      const translationKey = `campaigns.types.${campaignType}`;
      return te(translationKey) ? t(translationKey) : campaignType;
    };

    const getCampaignTypeIcon = (campaignType: string): string => {
      const icons: Record<string, string> = {
        bulk: 'fa-solid fa-paper-plane',
        automation: 'fa-solid fa-rotate',
      };
      return icons[campaignType] || 'fa-solid fa-question';
    };

    const getCampaignTypeColors = (campaignType: string): { bg: string; color: string; borderColor?: string } => {
      if (campaignType === 'bulk') {
        return {
          bg: '#e0f2fe',
          color: '#0369a1',
          borderColor: '#bae6fd',
        };
      }
      if (campaignType === 'automation') {
        return {
          bg: '#f3e8ff',
          color: '#7c3aed',
          borderColor: '#e9d5ff',
        };
      }
      return {
        bg: 'var(--surface-200)',
        color: 'var(--text-color)',
      };
    };

    /**
     * Résout le type de campagne (automation/bulk) pour la colonne
     * `campaign_name`. Le template Vue ne supportant pas les casts TS
     * inline (cf. feedback-vue-template-inline-typing), on encapsule la
     * logique ici.
     *   - Combined : `data.campaign_type` est exposé par la UNION ALL.
     *   - Mono-type (automation/bulk) : la colonne porte `subType` posé
     *     par les helpers du backend (cf. campaignNameColumn.ts).
     */
    const resolveCampaignNameType = (data: any, column: any): string => {
      if (data?.campaign_type) return String(data.campaign_type);
      if (column?.subType) return String(column.subType);
      return '';
    };

    // Helper to get cell value from data and column
    const getCellValue = (data: any, column: SpmColumnConfig): string => {
      if (column.fields.length === 1) {
        const fieldName = typeof column.fields[0] === 'string' ? column.fields[0] : column.fields[0].name;
        return data[fieldName] ?? '';
      }
      return column.fields
        .map((f) => {
          const fieldName = typeof f === 'string' ? f : f.name;
          return data[fieldName];
        })
        .filter(Boolean)
        .join(' ');
    };

    // Helper to determine if a cell value should be rendered as HTML
    const shouldRenderAsHtml = (data: any, column: SpmColumnConfig): boolean => {
      const value = getCellValue(data, column);
      // Check if the value contains HTML tags
      return typeof value === 'string' && (value.includes('<div') || value.includes('<span') || value.includes('<'));
    };

    // Computed property to get all dynamic columns (excluding custom slots)
    const dynamicColumns = computed(() => {
      // Exclude columns that already have custom slots defined
      const customSlotColumns = ['fullname', 'email', 'campaign_type', 'campaign_name', 'channel'];
      return currentColumns.value.filter((col) => !customSlotColumns.includes(col.id));
    });

    /**
     * Auto-detect data-key from columns
     * Looks for the first field that matches ID patterns (id, id_*, *_id)
     */
    const tableDataKey = computed(() => {
      // First, try to find a column explicitly marked as ID
      const idColumn = currentColumns.value.find((col) => col.id === 'id' || col.hidden);
      if (idColumn && idColumn.fields && idColumn.fields.length > 0) {
        const fieldName = typeof idColumn.fields[0] === 'string' ? idColumn.fields[0] : idColumn.fields[0].name;
        if (fieldName) return fieldName;
      }

      // Fallback: look for any field that looks like an ID
      const idLikeColumn = currentColumns.value.find((col) => {
        if (col.fields && col.fields.length > 0) {
          const fieldName = typeof col.fields[0] === 'string' ? col.fields[0] : col.fields[0].name;
          return fieldName && (fieldName === 'id' || fieldName.startsWith('id_') || fieldName.endsWith('_id'));
        }
        return false;
      });

      if (idLikeColumn && idLikeColumn.fields && idLikeColumn.fields.length > 0) {
        const fieldName = typeof idLikeColumn.fields[0] === 'string' ? idLikeColumn.fields[0] : idLikeColumn.fields[0].name;
        if (fieldName) return fieldName;
      }

      // Ultimate fallback
      return 'id';
    });

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          params: buildRequestPayload(),
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );

        if (response && !response.error) {
          await showToastSuccess(t('myLists.manage.customers.export.success'));
        } else {
          await showToastError(t('myLists.manage.customers.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      } finally {
        setLoading(false);
      }
    };

    return {
      t,
      isSpmTable,
      isSpmKpi,
      chartData,
      kpiData,
      statLoaded,
      unavailableForShop,
      startDate,
      endDate,
      widgetName,
      permissionToWidgetData,
      trialAvailable,
      acceptTTC,
      showTACModal,
      handleUpgradeButton,
      handleTryPlanButton,
      findPermission,
      // New for SpmTableAPI dynamic mode
      spmTableRef,
      fetchDynamicTableData,
      statsTableRowActions,
      saleAttributionModalVisible,
      saleAttributionIdShopOrder,
      openContactFileFromRow,
      canOpenContactFile,
      // Helper functions
      cleanStr,
      shouldShowFirstName,
      shouldShowLastName,
      shouldShowDateInFullname,
      mainDateMeta,
      getDisplayName,
      getChannelLabel,
      getChannelIcon,
      getChannelColors,
      getCampaignTypeLabel,
      getCampaignTypeIcon,
      getCampaignTypeColors,
      resolveCampaignNameType,
      // HTML rendering helpers
      getCellValue,
      shouldRenderAsHtml,
      dynamicColumns,
      // Auto-detected data-key
      tableDataKey,
      // Export
      handleExport,
    };
  },
});
</script>

<style scoped lang="scss">
.stats-widget-data-root {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.grid {
  height: 100%;
}

.p-card {
  box-shadow: none;
  height: 100%;
}

:deep() .card-skeleton,
.graph-container {
  height: 100%;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
}
.star--premium {
  color: $silver;
  width: 25px;
  height: 25px;
}
.star--ultimate {
  color: $gold;
  width: 25px;
  height: 25px;
}

/* Fullname composite column */
.fullname-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fullname-cell .name-row {
  display: flex;
  align-items: center;
}

.fullname-cell .contact-name {
  font-weight: 600;
  color: #1f2937;
}

.fullname-cell .contact-name--empty {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
}

/* Lien inline « ouvrir la fiche contact » à côté du nom. Discret par
   défaut (gris), apparait au survol de la ligne via le focus visuel. */
.fullname-cell .open-contact-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: 6px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  font-size: 0.78rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.fullname-cell .open-contact-link:hover,
.fullname-cell .open-contact-link:focus-visible {
  background-color: rgba(0, 0, 0, 0.06);
  color: #2563eb;
  outline: none;
}

.fullname-cell .dates-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #6b7280;
  flex-wrap: wrap;
}

.fullname-cell .dates-row__icon {
  color: #9ca3af;
}

.fullname-cell .dates-row__label {
  font-weight: 500;
  color: #6b7280;
}

/* Empty cell */
.cell-empty {
  color: #9ca3af;
}

.cursor-pointer {
  cursor: pointer;
}

/* HTML content in table cells */
:deep(.spm-table-apiV2 td) {
  div[style*="color"] {
    display: inline;
    font-size: 0.875rem;
  }
}
</style>
